import {
  Fragment,
  useEffect,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import { type QueryClient } from '@tanstack/react-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';

import { SplashPage } from '#apps/client-web/pages/splash';
import { type AppSettingsService } from '#apps/client-web/features/settings/services/app-settings';
import { useLoadAppSettingsQuery } from '#apps/client-web/features/settings/hooks/use-load-app-settings-query';

export type AppBootstrapProps = {
  /**
   * Service to load user settings
   */
  appSettingsService: AppSettingsService;

  /**
   * Query client instance
   */
  queryClient: QueryClient;
};

export const AppBootstrap: FC<
  PropsWithChildren<AppBootstrapProps & LoggingContext>
> = ({ appSettingsService, queryClient, logger, children }) => {
  const [phase, setPhase] = useState<'loading' | 'timeout' | 'ready'>(
    'loading',
  );

  const loadAppSettingsQuery = useLoadAppSettingsQuery({
    appSettingsService,
    queryClient,
  });

  // wait for all queries to be ready
  useEffect(() => {
    const queryStatuses = [loadAppSettingsQuery.status];

    const isReady = queryStatuses.every((status) => status === 'success');

    if (isReady) {
      logger?.debug('App settings loaded');
      setPhase('timeout');
    }
  }, [loadAppSettingsQuery.status]);

  // Small delay to show splash page
  useEffect(() => {
    // wait for timeout phase
    if (phase !== 'timeout') {
      return;
    }

    const timeout = setTimeout(() => {
      logger?.debug('App is ready');
      setPhase('ready');
    }, 1500);

    return () => {
      clearTimeout(timeout);
    };
  }, [phase]);

  return phase === 'ready' ? <Fragment>{children}</Fragment> : <SplashPage />;
};
